import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, TrendingUp, AlertTriangle, CheckCircle, ChevronDown, ChevronRight, DollarSign, ExternalLink, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAppContext } from "@/lib/onboardingContext";
import { getBudgetItemsForType, calculateBudgetTotals, applyMarketMultipliers, type BudgetCategory } from "@/lib/budgetItems";
import { getCurrencySymbol, convertFromUSD } from "@/lib/currency";
import { handleExternalClick } from "@/lib/externalLink";
import { getCityProfile } from "@/lib/marketData";

interface VendorLink {
  name: string;
  url: string;
  note: string;
}

const VENDOR_LINKS: Record<string, VendorLink[]> = {
  "Commercial Range / Stove": [
    { name: "WebstaurantStore", url: "https://www.webstaurantstore.com/search/commercial-range.html", note: "Largest selection, wholesale prices" },
    { name: "KaTom", url: "https://www.katom.com/cat/ranges.html", note: "Free shipping over $500" },
  ],
  "Commercial Refrigerator": [
    { name: "WebstaurantStore", url: "https://www.webstaurantstore.com/search/commercial-refrigerator.html", note: "New & refurbished options" },
    { name: "Amazon Business", url: "https://www.amazon.com/s?k=commercial+refrigerator", note: "Fast delivery, reviews" },
  ],
  "Freezer": [
    { name: "WebstaurantStore", url: "https://www.webstaurantstore.com/search/commercial-freezer.html", note: "Chest & upright options" },
  ],
  "Commercial Dishwasher": [
    { name: "WebstaurantStore", url: "https://www.webstaurantstore.com/search/commercial-dishwasher.html", note: "High-temp & chemical models" },
    { name: "KaTom", url: "https://www.katom.com/cat/dishwashers.html", note: "Price match guarantee" },
  ],
  "Exhaust Hood & Fire Suppression": [
    { name: "HoodMart", url: "https://www.hoodmart.com/", note: "Custom hoods, free design help" },
    { name: "CKitchen", url: "https://www.ckitchen.com/ventilation-hoods.html", note: "Budget-friendly options" },
  ],
  "Ice Machine": [
    { name: "WebstaurantStore", url: "https://www.webstaurantstore.com/search/ice-machine.html", note: "Top brands: Manitowoc, Scotsman" },
  ],
  "POS System": [
    { name: "Square", url: "https://squareup.com/us/en/restaurants", note: "Free plan available" },
    { name: "Toast", url: "https://pos.toasttab.com/", note: "Built for restaurants, $0 starter" },
    { name: "Clover", url: "https://www.clover.com/pos-systems/restaurants", note: "Flexible hardware" },
  ],
  "Tables & Chairs": [
    { name: "Restaurant Furniture Plus", url: "https://www.restaurantfurnitureplus.com/", note: "Bulk discounts" },
    { name: "Webstaurant Furniture", url: "https://www.webstaurantstore.com/search/restaurant-tables-chairs.html", note: "Wide selection" },
  ],
  "Logo & Brand Design": [
    { name: "99designs", url: "https://99designs.com/", note: "From $299, money-back guarantee" },
    { name: "Fiverr", url: "https://www.fiverr.com/categories/graphics-design/creative-logo-design", note: "From $10, fast turnaround" },
    { name: "Canva", url: "https://www.canva.com/create/logos/", note: "Free DIY option" },
  ],
  "Website": [
    { name: "Squarespace", url: "https://www.squarespace.com/restaurants", note: "Restaurant templates, $16/mo" },
    { name: "Wix Restaurants", url: "https://www.wix.com/restaurant/website", note: "Free plan available" },
  ],
  "General Liability Insurance": [
    { name: "Next Insurance", url: "https://www.nextinsurance.com/restaurant-insurance/", note: "Online quotes in 10 min" },
    { name: "Hiscox", url: "https://www.hiscox.com/small-business-insurance/restaurant-insurance", note: "Tailored for small business" },
  ],
  "Workers' Comp Insurance": [
    { name: "Next Insurance", url: "https://www.nextinsurance.com/workers-compensation/", note: "Monthly payments, no deposit" },
    { name: "The Hartford", url: "https://www.thehartford.com/workers-compensation", note: "AM Best A+ rated" },
  ],
  "Espresso Machine": [
    { name: "Clive Coffee", url: "https://clivecoffee.com/collections/commercial-espresso-machines", note: "La Marzocca authorized dealer" },
    { name: "Prima Coffee", url: "https://www.primacoffee.com/c/commercial-espresso-machines", note: "Expert recommendations" },
    { name: "WebstaurantStore", url: "https://www.webstaurantstore.com/search/espresso-machine-commercial.html", note: "Budget options available" },
  ],
  "Coffee Grinder (2)": [
    { name: "Prima Coffee", url: "https://www.primacoffee.com/c/commercial-coffee-grinders", note: "Baratza, Mahlkönig, Eureka" },
  ],
  "Security Cameras": [
    { name: "Amazon Business", url: "https://www.amazon.com/s?k=commercial+security+camera+system", note: "Reolink, Lorex systems" },
    { name: "Costco Business", url: "https://www.costco.com/security-cameras-surveillance.html", note: "Member pricing" },
  ],
  "Accounting Software": [
    { name: "QuickBooks", url: "https://quickbooks.intuit.com/", note: "Industry standard, $30/mo" },
    { name: "Wave", url: "https://www.waveapps.com/", note: "100% free" },
    { name: "Xero", url: "https://www.xero.com/", note: "Great for multi-location" },
  ],
  "Signage (exterior)": [
    { name: "Signs.com", url: "https://www.signs.com/", note: "Custom signs, free design" },
    { name: "Fastsigns", url: "https://www.fastsigns.com/", note: "Local installation available" },
  ],
  "Grand Opening Marketing": [
    { name: "Canva", url: "https://www.canva.com/", note: "Free templates for flyers & social" },
    { name: "Mailchimp", url: "https://mailchimp.com/", note: "Free email marketing up to 500 contacts" },
  ],
  "Bar Construction": [
    { name: "BarProducts.com", url: "https://www.barproducts.com/", note: "Bar supplies & equipment" },
  ],
  "Back Bar Coolers (2–3)": [
    { name: "WebstaurantStore", url: "https://www.webstaurantstore.com/search/back-bar-cooler.html", note: "Glass door display coolers" },
  ],
  "Draft Beer System": [
    { name: "Beverage Factory", url: "https://www.beveragefactory.com/draft-beer-systems", note: "Complete kegerator systems" },
    { name: "MicroMatic", url: "https://www.micromatic.com/", note: "Professional-grade draft systems" },
  ],
  "Commercial Deck Oven": [
    { name: "WebstaurantStore", url: "https://www.webstaurantstore.com/search/deck-oven.html", note: "Electric & gas options" },
    { name: "KaTom", url: "https://www.katom.com/cat/ovens.html", note: "Free shipping available" },
  ],
  "Commercial Mixer (20–60 qt)": [
    { name: "WebstaurantStore", url: "https://www.webstaurantstore.com/search/commercial-mixer.html", note: "Hobart, Globe, and more" },
  ],
  "Food Truck (used)": [
    { name: "Roaming Hunger", url: "https://roaminghunger.com/marketplace/", note: "Verified used food trucks" },
    { name: "UsedVending", url: "https://www.usedvending.com/food-trucks", note: "Large marketplace" },
  ],
  "Prep Tables (stainless)": [
    { name: "WebstaurantStore", url: "https://www.webstaurantstore.com/search/stainless-steel-prep-table.html", note: "NSF certified" },
  ],
  "Smallwares (pots, pans, utensils)": [
    { name: "WebstaurantStore", url: "https://www.webstaurantstore.com/search/restaurant-smallwares.html", note: "Bulk pricing" },
    { name: "Restaurant Depot", url: "https://www.restaurantdepot.com/", note: "Cash & carry, membership required" },
  ],
  "Food Inventory (first 2 weeks)": [
    { name: "US Foods", url: "https://www.usfoods.com/", note: "Nationwide distributor" },
    { name: "Sysco", url: "https://www.sysco.com/", note: "Largest foodservice distributor" },
    { name: "Restaurant Depot", url: "https://www.restaurantdepot.com/", note: "Cash & carry wholesale" },
  ],
  "Dinnerware, Glasses, Flatware": [
    { name: "WebstaurantStore", url: "https://www.webstaurantstore.com/search/dinnerware.html", note: "Restaurant-grade sets" },
  ],
  "ServSafe Certification": [
    { name: "ServSafe", url: "https://www.servsafe.com/", note: "Official certification program" },
  ],
  "Menu / Lookbook Printing": [
    { name: "Canva", url: "https://www.canva.com/menus/", note: "Free menu templates" },
    { name: "Vistaprint", url: "https://www.vistaprint.com/menus", note: "Professional printing" },
  ],
};

function getVendorsForItem(itemName: string): VendorLink[] {
  return VENDOR_LINKS[itemName] || [];
}

export default function BudgetPage() {
  const navigate = useNavigate();
  const { results: rawResults, onboarding, selectedNeighborhood, ensureResults } = useAppContext();
  const results = rawResults ?? ensureResults();

  const city = (onboarding.city || "").toLowerCase();
  const cs = getCurrencySymbol(city);
  const cityProfile = getCityProfile(city);
  const fmtLocal = (n: number) => `${cs}${convertFromUSD(n, city).toLocaleString()}`;
  const activeNeighborhood = selectedNeighborhood?.cityKey === city.trim() ? selectedNeighborhood : null;
  
  // Get base budget items and apply market-specific multipliers
  const rawCategories = getBudgetItemsForType(onboarding.businessType);
  
  // Derive a general cost multiplier from rent and wage (weighted average)
  const generalMult = (cityProfile.rentMultiplier * 0.4 + cityProfile.wageMultiplier * 0.3 + 1.0 * 0.3);
  // Buildout uses avgBuildoutPerSqft relative to US average (~110)
  const buildoutMult = cityProfile.avgBuildoutPerSqft / 110;
  
  const targetSqft = onboarding.sqftMax || onboarding.sqftMin || 1200;
  let categories = applyMarketMultipliers(rawCategories, {
    rentMultiplier: cityProfile.rentMultiplier,
    wageMultiplier: cityProfile.wageMultiplier,
    buildoutMultiplier: buildoutMult,
    generalMultiplier: generalMult,
  }, targetSqft, onboarding.businessType);

  // ─── Neighborhood-driven rent override ─────────────────────────────
  // When a neighborhood is selected, replace the rent-based items in the
  // "Lease & Deposits" category with values derived from the midpoint of the
  // neighborhood's rent range × venue size. monthlyRentLocal is already in
  // local currency; budget items are stored in USD, so convert local → USD.
  if (activeNeighborhood) {
    const rate = convertFromUSD(1, city) || 1;
    const monthlyRentUSD = Math.round(activeNeighborhood.monthlyRentLocal / rate);
    categories = categories.map(cat => {
      if (!/lease|deposit|rent|property acquisition/i.test(cat.category)) return cat;
      return {
        ...cat,
        items: cat.items.map(item => {
          const name = item.item.toLowerCase();
          // 2 months security deposit
          if (name.includes("security deposit")) {
            const v = monthlyRentUSD * 2;
            return { ...item, lowEstimate: v, highEstimate: v, note: "2× neighborhood monthly rent" };
          }
          // First / last month rent (handle combined or separate)
          if (name.includes("first") && name.includes("last")) {
            const v = monthlyRentUSD * 2;
            return { ...item, lowEstimate: v, highEstimate: v, note: "First + last month, neighborhood rate" };
          }
          if (name.includes("first month")) {
            return { ...item, lowEstimate: monthlyRentUSD, highEstimate: monthlyRentUSD, note: "Neighborhood monthly rate" };
          }
          if (name.includes("last month")) {
            return { ...item, lowEstimate: monthlyRentUSD, highEstimate: monthlyRentUSD, note: "Neighborhood monthly rate" };
          }
          // Broker fee — typically 1 month's rent
          if (name.includes("broker")) {
            return { ...item, lowEstimate: monthlyRentUSD, highEstimate: monthlyRentUSD, note: "≈ 1 month neighborhood rent" };
          }
          return item;
        }),
      };
    });
  }

  let { totalLow, totalHigh } = calculateBudgetTotals(categories);

  // Enforce Miami floor of $150K
  const isMiami = city.includes("miami");
  if (isMiami) {
    if (totalLow < 150000) totalLow = 150000;
    if (totalHigh < totalLow) totalHigh = totalLow;
  }

  const midEstimate = Math.round((totalLow + totalHigh) / 2);

  const budgetValue = results.totalEstimate;
  const budgetFit = midEstimate <= budgetValue ? "strong" : midEstimate <= budgetValue * 1.3 ? "balanced" : "underfunded";
  const fitColor = budgetFit === "strong" ? "text-primary" : budgetFit === "balanced" ? "text-accent" : "text-destructive";
  const FitIcon = budgetFit === "strong" ? CheckCircle : budgetFit === "balanced" ? TrendingUp : AlertTriangle;
  const fitMsg = budgetFit === "strong"
    ? "Your budget looks strong for this type of business."
    : budgetFit === "balanced"
    ? "Your budget is workable but leave room for surprises."
    : "Your budget may be tight. Consider a smaller format or lower-cost area.";

  // Show warning if onboarding budget is under $100K and city is Miami
  const showMiamiBudgetWarning = isMiami && budgetValue < 100000;

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="border-b border-border bg-card">
        <div className="container py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-lg font-medium text-foreground">Budget Planner</h1>
            <p className="text-sm text-muted-foreground">Itemized startup costs for a {results.businessLabel}</p>
          </div>
        </div>
      </div>

      <div className="container pt-8 max-w-2xl">
        {/* Miami budget warning */}
        {showMiamiBudgetWarning && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl p-4 mb-6 flex items-start gap-3 border bg-amber-500/10 border-amber-500/30"
          >
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-amber-600" />
            <div>
              <h3 className="font-medium text-amber-600">Budget Advisory</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Heads up — opening a restaurant in Miami typically requires at least {fmtLocal(150000)} in startup capital. Consider adjusting your budget before proceeding.
              </p>
            </div>
          </motion.div>
        )}

        {/* Budget fit banner */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-xl p-4 mb-6 flex items-start gap-3 border ${
            budgetFit === "strong" ? "bg-primary/5 border-primary/20" :
            budgetFit === "balanced" ? "bg-accent/5 border-accent/20" :
            "bg-destructive/5 border-destructive/20"
          }`}
        >
          <FitIcon className={`w-5 h-5 shrink-0 mt-0.5 ${fitColor}`} />
          <div>
            <h3 className={`font-medium ${fitColor}`}>
              {budgetFit.charAt(0).toUpperCase() + budgetFit.slice(1)} Budget
            </h3>
            <p className="text-sm text-muted-foreground mt-1">{fitMsg}</p>
          </div>
        </motion.div>

        {/* Neighborhood-applied rent banner */}
        {activeNeighborhood && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 rounded-lg border-2 border-primary/30 bg-primary/5 flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <p className="text-xs text-foreground">
              <span className="font-medium">Rent updated based on {activeNeighborhood.name}</span> — adjust manually if needed.
              <span className="text-muted-foreground"> ~{cs}{activeNeighborhood.monthlyRentLocal.toLocaleString()}/mo will flow into your Forecast.</span>
            </p>
          </motion.div>
        )}

        {/* Total range */}
        <div className="text-center mb-8 bg-card border border-border rounded-xl p-6">
          <p className="text-sm text-muted-foreground mb-1">Estimated Total Range</p>
          <p className="font-heading text-2xl font-medium text-foreground">
            {fmtLocal(totalLow)} – {fmtLocal(totalHigh)}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Based on typical costs for a {results.businessLabel.toLowerCase()} in {cityProfile.city}. Adjusted for local rent, labor, and construction costs.
          </p>
        </div>

        {/* Itemized breakdown */}
        <div className="space-y-3">
          {categories.map((cat, i) => (
            <CategorySection key={`${cat.category}-${i}`} category={cat} index={i} city={city} />
          ))}
        </div>

        {/* Alcohol License Guidance */}
        <AlcoholLicenseGuidance businessType={onboarding.businessType} city={city} currencySymbol={cs} />

        <div className="mt-8 p-4 rounded-xl bg-muted/30 border border-border">
          <p className="text-xs text-muted-foreground">
            ⚠️ <strong>Disclaimer:</strong> These are estimated ranges adjusted to the {cityProfile.city} market using local rent, labor, and construction cost indices. Actual costs vary by exact location, condition of space, equipment quality (new vs used), and vendor. Always get 3+ quotes before committing.
          </p>
        </div>
      </div>
    </div>
  );
}

const ALCOHOL_TYPES = ["restaurant", "bar", "hotel", "boutique-hotel", "wine-shop", "brunch-spot"];

function getAlcoholLicenseData(city: string) {
  const c = city.toLowerCase();
  if (c.includes("miami") || c.includes("florida")) {
    return {
      cityLabel: "Miami / Florida",
      licenses: [
        { type: "Full Liquor License", cost: "$400,000 – $800,000 (quota) or ~$1,820/yr (SRX)", timeline: "3–6 months", note: "Florida quota licenses are limited and traded on the open market. SRX (Special Restaurant) licenses are available to restaurants with 150+ seats and 51%+ food revenue." },
        { type: "Beer & Wine Only", cost: "$1,820/yr", timeline: "30–60 days", note: "Standard 2COP license. Allows sale of beer and wine only — no spirits. Good for cafés, bakeries, and casual dining." },
        { type: "No Alcohol", cost: "$0", timeline: "N/A", note: "No liquor license needed. Simplifies permitting and reduces liability insurance costs." },
      ],
    };
  }
  if (c.includes("new york") || c.includes("nyc") || c.includes("manhattan") || c.includes("brooklyn")) {
    return {
      cityLabel: "New York",
      licenses: [
        { type: "Full Liquor License", cost: "$4,500 (application) + $1,200–$3,000 in fees", timeline: "4–8 months", note: "New York State Liquor Authority (SLA) issues licenses. Process involves community board review and 30-day posting period." },
        { type: "Beer & Wine Only", cost: "$2,500–$3,500", timeline: "2–4 months", note: "Tavern Wine license. Faster approval, no community board review required." },
        { type: "No Alcohol", cost: "$0", timeline: "N/A", note: "No license needed." },
      ],
    };
  }
  if (c.includes("london") || c.includes("uk") || c.includes("england")) {
    return {
      cityLabel: "London / UK",
      licenses: [
        { type: "Premises Licence (Full)", cost: "£190–£1,905 (based on rateable value)", timeline: "4–8 weeks", note: "Applied through local council. Covers alcohol sale, late-night refreshment, and entertainment. Requires a Designated Premises Supervisor with a Personal Licence." },
        { type: "Beer & Wine Only", cost: "£190–£635", timeline: "3–6 weeks", note: "Same premises licence but restricted to lower-strength beverages. Simpler approval." },
        { type: "No Alcohol", cost: "£0", timeline: "N/A", note: "No licence needed." },
      ],
    };
  }
  if (c.includes("dubai") || c.includes("abu dhabi") || c.includes("uae")) {
    return {
      cityLabel: "Dubai / UAE",
      licenses: [
        { type: "Alcohol Permit (Hotel/Restaurant)", cost: "AED 20,000–50,000/yr", timeline: "2–4 months", note: "Only available for hotels, hotel-restaurants, and licensed clubs. Requires Tourism License (DTCM) and DED Trade License." },
        { type: "Limited (Beer & Wine)", cost: "AED 10,000–25,000/yr", timeline: "1–3 months", note: "Restricted menu. Must be within a hotel or licensed venue." },
        { type: "No Alcohol", cost: "AED 0", timeline: "N/A", note: "No permit needed. Many successful F&B concepts operate without alcohol." },
      ],
    };
  }
  if (c.includes("austin") || c.includes("texas")) {
    return {
      cityLabel: "Austin / Texas",
      licenses: [
        { type: "Mixed Beverage (Full Liquor)", cost: "$6,068 (initial) + $3,034/yr renewal", timeline: "6–12 weeks", note: "TABC Mixed Beverage permit. Required for spirits. Must have food service generating 51%+ revenue to qualify (Restaurant designation)." },
        { type: "Beer & Wine Only", cost: "$1,525 (initial) + $762/yr renewal", timeline: "4–8 weeks", note: "TABC Beer & Wine permit. Simpler process, lower cost." },
        { type: "No Alcohol", cost: "$0", timeline: "N/A", note: "No permit needed." },
      ],
    };
  }
  // Generic fallback
  return {
    cityLabel: "your city",
    licenses: [
      { type: "Full Liquor License", cost: "$3,000–$15,000/yr (varies widely)", timeline: "2–6 months", note: "Cost varies dramatically by state/country. Some jurisdictions use quota systems where licenses trade for $100K+. Check your local alcohol control board." },
      { type: "Beer & Wine Only", cost: "$1,000–$5,000/yr", timeline: "1–3 months", note: "Lower cost and faster approval than full liquor. Good option for restaurants where spirits aren't essential to the concept." },
      { type: "No Alcohol", cost: "$0", timeline: "N/A", note: "Skip the license entirely. Reduces insurance costs, simplifies compliance, and speeds up your opening timeline." },
    ],
  };
}

function AlcoholLicenseGuidance({ businessType, city, currencySymbol }: { businessType: string; city: string; currencySymbol: string }) {
  const servesAlcohol = ALCOHOL_TYPES.includes(businessType);
  if (!servesAlcohol) return null;

  const data = getAlcoholLicenseData(city);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-6 bg-card border border-border rounded-xl overflow-hidden"
    >
      <div className="p-4 border-b border-border bg-muted/20">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-accent" />
          <h3 className="font-heading font-medium text-foreground text-sm">Alcohol License Options — {data.cityLabel}</h3>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Your business type typically serves alcohol. Here are the licensing options for {data.cityLabel}, with estimated costs and timelines.
        </p>
      </div>
      <div className="divide-y divide-border">
        {data.licenses.map((lic) => (
          <div key={lic.type} className="p-4">
            <div className="flex items-start justify-between gap-3 mb-1">
              <div className="flex items-center gap-2">
                <DollarSign className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="text-sm font-medium text-foreground">{lic.type}</span>
              </div>
              <span className="text-sm font-medium text-foreground whitespace-nowrap">{lic.cost}</span>
            </div>
            <div className="ml-5.5 space-y-1">
              <div className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Timeline:</span> {lic.timeline}
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{lic.note}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="p-4 bg-muted/10 border-t border-border">
        <p className="text-xs text-muted-foreground leading-relaxed">
          <strong>Quick guide:</strong> A <em>Full Liquor License</em> allows sale of all spirits, beer, and wine — typically the most expensive and slowest to obtain. A <em>Beer &amp; Wine Only</em> license is cheaper and faster but limits you to lower-margin drinks. <em>No Alcohol</em> skips the license entirely, lowering insurance and speeding up your opening.
        </p>
      </div>
    </motion.div>
  );
}

function CategorySection({ category, index, city }: { category: BudgetCategory; index: number; city: string }) {
  const [open, setOpen] = useState(index < 3);
  const cs = getCurrencySymbol(city);
  const catLow = category.items.filter(i => i.included).reduce((s, i) => s + i.lowEstimate, 0);
  const catHigh = category.items.filter(i => i.included).reduce((s, i) => s + i.highEstimate, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className="bg-card border border-border rounded-xl overflow-hidden"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/20 transition-colors"
      >
        <div className="flex items-center gap-3">
          {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          <span className="font-heading font-medium text-foreground text-sm">{category.category}</span>
          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{category.items.length} items</span>
        </div>
        <span className="text-sm font-medium text-foreground">
          {cs}{convertFromUSD(catLow, city).toLocaleString()} – {cs}{convertFromUSD(catHigh, city).toLocaleString()}
        </span>
      </button>

      {open && (
        <div className="border-t border-border divide-y divide-border/50">
          {category.items.map((item, j) => {
            const vendors = getVendorsForItem(item.item);
            return (
              <div key={j} className="px-4 py-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-3 h-3 text-primary shrink-0" />
                      <span className="text-sm font-medium text-foreground">{item.item}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 ml-5">{item.note}</p>
                  </div>
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    {item.lowEstimate === 0 && item.highEstimate === 0
                      ? "Free"
                      : `${cs}${convertFromUSD(item.lowEstimate, city).toLocaleString()} – ${cs}${convertFromUSD(item.highEstimate, city).toLocaleString()}`
                    }
                  </span>
                </div>
                {vendors.length > 0 && (
                  <div className="ml-5 mt-2 flex flex-wrap gap-1.5">
                    {vendors.map((v, vi) => (
                      <a
                        key={vi}
                        href={v.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleExternalClick}
                        title={v.note}
                        className="inline-flex items-center gap-1 text-xs bg-primary/5 text-primary hover:bg-primary/10 px-2 py-1 rounded-md transition-colors"
                      >
                        <ShoppingCart className="w-3 h-3" />
                        {v.name}
                        <ExternalLink className="w-2.5 h-2.5 opacity-50" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
